import { useEffect, useMemo, useState } from 'react';
import { db } from './firebase';
import { collection, getCountFromServer } from 'firebase/firestore';
import { AnswerMap } from './answerLog';

export type ProgressMap = Record<string, { total: number; done: number }>;


// 進度：done＝答對過的題數（逐題作答紀錄，不重複計算）；total＝題庫題數
export function useProgress(
  answers: AnswerMap,
  topicsMap: Record<string, string[]>,
  cacheTag = 'all',   // 不同範圍（登入／未登入）分開快取，避免互相覆蓋
  stored: Record<string, Record<string, number>> = {},   // 科目資料裡存好的題數（customSubjects.counts）；沒有的主題才即時查詢
) {
  const [totalMap, setTotalMap] = useState<Record<string, number>>({});

  // 快取識別：科目與其主題 ID 都納入，主題增刪或改用 ID 後會重新計數
  const topicsKey = JSON.stringify(Object.keys(topicsMap).sort().map(sid => [sid, topicsMap[sid]]));

  const storedKey = JSON.stringify(stored);

  // Load total question counts：科目資料有存題數就直接用；缺的主題才向資料庫查詢
  //（查詢結果快取於 localStorage，主題變動或超過 6 小時重新計數）
  useEffect(() => {
    if (!Object.keys(topicsMap).length) return;
    const known = (sid: string, t: string) => stored[sid]?.[t];
    if (Object.entries(topicsMap).every(([sid, ts]) => ts.every(t => known(sid, t) !== undefined))) {
      const map: Record<string, number> = {};
      Object.entries(topicsMap).forEach(([sid, ts]) => {
        ts.forEach(t => { map[`${sid}/${t}`] = known(sid, t)!; });
        map[sid] = ts.reduce((a, t) => a + known(sid, t)!, 0);
      });
      setTotalMap(map);
      return;
    }
    const CACHE_KEY = `gsat_total_map_v7_${cacheTag}`;
    const TTL = 6 * 60 * 60 * 1000;
    try {
      // 清除舊版快取
      Object.keys(localStorage).filter(k => k.startsWith('gsat_total_map_') && !k.startsWith('gsat_total_map_v7_')).forEach(k => localStorage.removeItem(k));
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.key === topicsKey + storedKey && Date.now() - cached.ts < TTL) { setTotalMap(cached.map); return; }
    } catch {}

    (async () => {
      const map: Record<string, number> = {};
      await Promise.all(Object.entries(topicsMap).map(async ([sid, topics]) => {
        const counts = await Promise.all(topics.map(async (topic) => {
          try {
            const n = known(sid, topic) ?? (await getCountFromServer(collection(db, 'questions', sid, 'topics', topic, 'items'))).data().count;
            map[`${sid}/${topic}`] = n;
            return n;
          } catch {
            return 0;
          }
        }));
        map[sid] = counts.reduce((a, b) => a + b, 0);
      }));
      setTotalMap(map);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ key: topicsKey + storedKey, ts: Date.now(), map })); } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicsKey, storedKey]);


  return useMemo(() => {
    const mastered = Object.values(answers).filter(a => a.corrects > 0);
    const progressMap: ProgressMap = {};
    for (const [key, total] of Object.entries(totalMap)) {
      const parts = key.split('/');
      const sid = parts[0];
      const done = mastered.filter(a => a.s === sid && (parts.length === 1 || a.t === parts[1])).length;
      progressMap[key] = { total, done: Math.min(done, total) };
    }
    return progressMap;
  }, [totalMap, answers]);
}
